"use client";

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Scroll } from "@/lib/supabase";

export type EditorTab = "write" | "html" | "css" | "js";
export type ActiveSmartView = "starred" | "archive" | null;

type AppState = {
  scrolls: Scroll[];
  sidebarOpen: boolean;
  activeFolderId: string | null;
  viewMode: "grid" | "list";
  editorTab: EditorTab;
  focusMode: boolean;
  saving: boolean;
  unsavedChanges: boolean;
  activeSmartView: ActiveSmartView;
  setScrolls: (scrolls: Scroll[] | ((prev: Scroll[]) => Scroll[])) => void;
  setSidebarOpen: (open: boolean) => void;
  setActiveFolderId: (folderId: string | null) => void;
  setViewMode: (mode: "grid" | "list") => void;
  setEditorTab: (tab: EditorTab) => void;
  setFocusMode: (focus: boolean) => void;
  setSaving: (saving: boolean) => void;
  setUnsavedChanges: (dirty: boolean) => void;
  setActiveSmartView: (view: ActiveSmartView) => void;
};

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      scrolls: [],
      sidebarOpen: true,
      activeFolderId: null,
      viewMode: "grid",
      editorTab: "write",
      focusMode: false,
      saving: false,
      unsavedChanges: false,
      activeSmartView: null,
      setScrolls: (scrolls) =>
        set((state) => ({
          scrolls:
            typeof scrolls === "function" ? scrolls(state.scrolls) : scrolls,
        })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      setActiveFolderId: (activeFolderId) => set({ activeFolderId }),
      setViewMode: (viewMode) => set({ viewMode }),
      setEditorTab: (editorTab) => set({ editorTab }),
      setFocusMode: (focusMode) => set({ focusMode }),
      setSaving: (saving) => set({ saving }),
      setUnsavedChanges: (unsavedChanges) => set({ unsavedChanges }),
      setActiveSmartView: (activeSmartView) => set({ activeSmartView }),
    }),
    {
      name: "inora-ui-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        activeFolderId: state.activeFolderId,
        viewMode: state.viewMode,
        editorTab: state.editorTab,
      }),
    },
  ),
);
